import {
  EnvironmentInjector,
  Injectable,
  Signal,
  runInInjectionContext,
  inject,
  signal,
} from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { toSignal } from '@angular/core/rxjs-interop';
import { DroppedWidgetIntoLayoutWidgetAction } from '@app/finxone-web-frontend/app/lib/actions/dropped.action';
import { WidgetLayoutPosition } from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { UUID } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  ActiveConfig,
  AlertTypes,
  CanvasZone,
  ComponentLabel,
  AccordionDefaultState,
  FormWidgetPlacementContext,
  FormsConfigType,
  GridPosition,
  HookAction,
  LayoutNames,
  NavigationOptionEnum,
  NewWorkflowWidgetConfig,
  NotLayoutWidgetErr,
  PageNavigationMap,
  PageTemplateDto,
  Role,
  SupportedDevice,
  TOP_LEVEL_ID,
  WORKFLOW_POPUPS,
  WidgetNames,
  WidgetStyleConst,
  WorkflowConfig,
  WorkflowPageConfig,
  WorkflowWidgetConfig,
  WorkflowWidgetConfigurationAttributes,
  WorkflowZoneConfig,
  WorkflowZoneDevicesConfig,
  ZONETYPEOPTION,
  addWidget,
  customDataQuestionDataType,
  customDataQuestionType,
  deleteWidget,
  findAndReplaceWidget,
  findWidget,
  flattenWorkflowWidgets,
  getFlattenedWidgetArray,
  getWidgetsArrayByLayoutTypeOrWidgetId,
  is2dArray,
  isLayoutWidget,
  moveWidget,
  onSetDefaultButtonGlobalStyling,
  questionProperties,
  updateChildComponentUniqueIds,
} from '@finxone-platform/shared/sys-config-types';
import {
  addDefaultContainerConfigData,
  containerConfigName,
  createPage,
  formatUrl,
  imageWidgetWidgetConfig,
  roleHooks,
  zoneHooks,
} from '@finxone-platform/shared/workflow-mapping';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { cloneDeep, merge } from 'lodash';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Observable, Subject, map, take, timer } from 'rxjs';
import { $enum } from 'ts-enum-util';
import { DisplayPopupAction, DroppedWidgetAction, NewWidgetEvent } from '../../actions/app-grid.action';
import { CopyWidget, CopyWidgets } from '../../actions/copy-paste.action';
import { AppGridState } from '../../state/app-grid.state';
import { ProjectsState } from '../../state/project.state';
import { ColumnCountChange, removeColumnFromMultiColumnWidget } from '../../utils/multi-column.utils';
import { ActiveConfigService } from '../active-config/active-config.service';
import { WorkflowConfigService } from '../workflow-config/workflow-config.service';
import {
  computeNumberOfInputs,
  removeFieldsFromProjectFormByIds,
} from '../workflow-form-editor/form-config.utils';
import { PageNavigationMapService } from '../page-navigation-map/page-navigation-map.service';

// TODO move to shared types
export type PageOverview = {
  pageId: string | undefined;
  url: string;
  pageName: string;
  title: string;
  fullUrl: string;
  role: string;
  zone: string;
  zoneFullName: string;
  // TODO remove once operating off page id
  zonePageIndex?: number;
  device: string;
  page: WorkflowPageConfig;
};

// TODO move to shared types
export type LinkIssue = {
  pageOverview: PageOverview;
  pageId: string | undefined;
  role: string;
  zone: string;
  device: string;
  url: string;
  pageName: string;
  pageTitle: string;
  action?: string;

  widgetDisplayName: string;
  widgetId: string;
  widgetName: string;
  errors: string[];
};

export type { ColumnCountChange };

@Injectable({
  providedIn: 'root',
})
export class WorkflowEditorService {
  @Select(AppGridState.getDraggedWidgetConfig)
  draggedWidgetConfig$: Observable<WorkflowWidgetConfig | null>;
  private textInputValueChanges = new Subject<{ value: string; uniqueId: string; key: string }>();
  public textInputValueChanges$ = this.textInputValueChanges.asObservable();
  /**
   * The multi column RHS config keeps its own copy of the column count, and it is not recreated while the
   * same widget stays selected. Set on column removals made outside the RHS (e.g. the LHS widget tree)
   * so that it can re-read the values. Cleared by the consumer via {@link consumeColumnCountChange} so a
   * later instance of that config cannot re-apply a snapshot that has since been superseded.
   */
  private readonly columnCountChangeState = signal<ColumnCountChange | null>(null);
  public readonly columnCountChange = this.columnCountChangeState.asReadonly();

  public draggedWidgetConfig: WorkflowWidgetConfig | null;
  private pendingWidgetDropContext: FormWidgetPlacementContext | null = null;

  activeConfig!: ActiveConfig;
  workflowConfig!: WorkflowConfig;

  /**
   * List of widget compatibility checks.
   * Each entry defines:
   * - widget: the widget being dropped
   * - incompatibleWith: widgets that, if present on the page, trigger the handler
   * - handler: function to execute when the rule matches
   */
  private widgetCompatibilityChecks = [
    {
      widget: 'quick_links_widget',
      incompatibleWith: ['form_widget'],
      handler: () => {
        this.alertHandlerService.showAlertFn(
          'warning',
          'Quicklinks will not validate the form before redirection. Use a Form Button widget.',
          'You have a form on this page',
        );
      },
    },
    // Add more compatibility rules here as needed, with custom handlers
  ];

  private store = inject(Store);
  private actions$ = inject(Actions);
  private injector = inject(EnvironmentInjector);
  public workflowConfigService = inject(WorkflowConfigService);
  private activeConfigService = inject(ActiveConfigService);
  private alertHandlerService = inject(AlertHandlerService);
  public ngxSmartModalService = inject(NgxSmartModalService);
  public pageNavigationMapService = inject(PageNavigationMapService);

  constructor() {
    this.setupActionDispatchListeners();

    this.draggedWidgetConfig$.subscribe((widgetConfig) => {
      this.draggedWidgetConfig = widgetConfig;
    });

    this.activeConfigService.getConfig().subscribe((activeConfig) => {
      this.activeConfig = activeConfig;
    });
    this.workflowConfigService.getConfig().subscribe((config) => {
      this.workflowConfig = config;
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)['workflowEditorService'] = this;
  }

  public updatePageConfigByUrlInZoneRoleDevice(
    url: string,
    zone: string,
    role: string,
    device: string,
    fn: (pageConfig: WorkflowPageConfig) => void,
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const pageConfig = this.getPageConfigByUrlInZoneRoleDevice(url, zone, role, device, workflowConfig);
      if (pageConfig) {
        fn(pageConfig);
      }
      return workflowConfig;
    });
  }

  public updatePageConfigByPageId(pageId: string, fn: (pageConfig: WorkflowPageConfig) => void) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const pageConfig = this.getPageConfigById(pageId, workflowConfig);
      if (pageConfig) {
        fn(pageConfig);
      }
      return workflowConfig;
    });
  }

  public getPageConfigByUrlInZoneRoleDevice(
    url: string,
    zone: string,
    role: string,
    device: string,
    workflowConfig?: WorkflowConfig,
  ): WorkflowPageConfig | null {
    const pageList = this.flattenPagesToList(workflowConfig || this.workflowConfig);
    return (
      pageList.find(
        (page) => page.url === url && page.zone === zone && page.role === role && page.device === device,
      )?.page || null
    );
  }

  public getPageConfigById(pageId: string, workflowConfig?: WorkflowConfig): WorkflowPageConfig | null {
    const pageList = this.flattenPagesToList(workflowConfig || this.workflowConfig);
    const page = pageList.find((page) => page.pageId === pageId)?.page || null;
    return page;
  }

  public pageExistsWithPageId(pageId: string): boolean {
    const pageList = this.flattenPagesToList(this.workflowConfig);
    return pageList.some((page) => page.pageId === pageId);
  }

  public pageExistsWithUrlInZoneRoleDevice(url: string, zone: string, role: string, device: string): boolean {
    const pageList = this.flattenPagesToList(this.workflowConfig);
    return pageList.some(
      (page) => page.url === url && page.zone === zone && page.role === role && page.device === device,
    );
  }

  public validateWorkflowPageConfigInRoleZoneAndDevice(
    page: WorkflowPageConfig,
    role: string,
    zone: string,
    device: string,
  ): { valid: boolean; errors: string[] } {
    const pageExists = this.pageExistsWithUrlInZoneRoleDevice(
      page.pageConfiguration?.url || '',
      zone,
      role,
      device,
    );
    if (pageExists) {
      return {
        valid: false,
        errors: ['A page with this URL already exists in the same zone, role, and device.'],
      };
    }

    if (!page.pageUniqueId) {
      return { valid: false, errors: ['Page ID is required.'] };
    }
    const pageIdExists = this.pageExistsWithPageId(page.pageUniqueId);
    if (pageIdExists) {
      return { valid: false, errors: ['A page with this Page ID already exists.'] };
    }
    return { valid: true, errors: [] };
  }

  public findLinksToNowhere() {
    const issues: LinkIssue[] = [];

    const pageList = this.flattenPagesToList(this.workflowConfig);
    pageList.forEach((page) => {
      const pageConfig = page.page;
      issues.push(...this.findLinkIssuesOnPage(page, pageConfig));
    });

    return issues;
  }

  private findLinkIssuesOnPage(pageOverview: PageOverview, pageConfig: WorkflowPageConfig) {
    const issues: LinkIssue[] = [];

    flattenWorkflowWidgets(pageConfig.widgets).forEach((widget) => {
      if (
        widget.name === WidgetNames.QUICK_LINKS_WIDGET ||
        widget.name === WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET
      ) {
        const result = this.validateQuickLink(pageOverview, widget);
        if (result) {
          issues.push(result);
        }
      }

      if (widget.name === WidgetNames.ACTION_BUTTON || widget.name === WidgetNames.FORM_SUBMISSION_BUTTON) {
        const result = this.validateActionButton(pageOverview, widget);
        if (result) {
          issues.push(result);
        }
      }
    });

    return issues;
  }

  findLinksToNowhereOnPage(pageId: string) {
    const pageList = this.flattenPagesToList(this.workflowConfig);
    const page = pageList.find((p) => p.pageId === pageId);

    if (!page) {
      return [];
    }

    return this.findLinkIssuesOnPage(page, page.page);
  }

  validateActionButton(pageOverview: PageOverview, widgetConfig: NewWorkflowWidgetConfig) {
    const errors: string[] = [];
    const actionConfigs =
      widgetConfig.widgetConfiguration.attributes.widgetProperties.actionNavigationConfigurations;

    for (const outcome of $enum(ActionButtonOutcomesType).getValues()) {
      if (actionConfigs?.[outcome]) {
        const role = actionConfigs[outcome]?.selectedRole;
        const zone = formatNameAsUrl(actionConfigs[outcome]?.selectedZone);
        const pageUrl = actionConfigs[outcome]?.selectedPageUrl;
        const externalUrl = actionConfigs[outcome]?.externalUrl;
        const selectedNavigationType = actionConfigs[outcome]?.selectedNavigationOption;

        if (zone === 'login') {
          continue;
        }
        if (externalUrl && selectedNavigationType === NavigationOptionEnum.USE_AS_EXTERNAL_LINK) {
          continue;
        }
        if (!zone || !role || !pageUrl) {
          errors.push(
            `On ${outcome} missing zone, role, or pageUrl attributes. zone: ${zone}, role: ${role}, pageUrl: ${pageUrl}, device: ${pageOverview.device}`,
          );
        }

        const linkedPage = this.getPageConfigByUrlInZoneRoleDevice(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          pageUrl!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          zone!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          role === 'EmptyRole' ? pageOverview.role : role!,
          pageOverview.device,
          this.workflowConfig,
        );
        if (!linkedPage) {
          errors.push(
            `On ${outcome} - Link does not work! Cannot find linked page: pageUrl: ${pageUrl}, zone: ${zone}, role: ${
              role === 'EmptyRole' ? 'EmptyRole so resolve: ' + pageOverview.role : role!
            }, device: ${pageOverview.device} navigationOption: ${selectedNavigationType} ${
              selectedNavigationType === NavigationOptionEnum.USE_AS_EXTERNAL_LINK
                ? `, externalUrl: ${externalUrl}`
                : ''
            }`,
          );
        }
      }
    }

    if (errors.length > 0) {
      return {
        pageOverview: pageOverview,
        pageId: pageOverview.pageId,
        pageName: pageOverview.pageName,
        pageTitle: pageOverview.title,
        widgetName: widgetConfig.name,
        widgetId: widgetConfig.uniqueId.toString(),
        widgetDisplayName: widgetConfig.widgetConfiguration.widgetDisplayName || '',
        role: pageOverview.role,
        zone: pageOverview.zone,
        action: widgetConfig.widgetConfiguration?.attributes?.widgetProperties?.buttonActionType,
        device: pageOverview.device,
        url: pageOverview.fullUrl,
        errors: errors,
      };
    } else {
      return null;
    }
  }

  private validateQuickLink(pageOverview: PageOverview, widgetConfig: NewWorkflowWidgetConfig) {
    const errors: string[] = [];
    const attributes = widgetConfig.widgetConfiguration.attributes;

    const zone = formatNameAsUrl(attributes?.['zone']);
    const role = attributes?.['role'];
    const pageUrl = attributes?.['pageUrl'];
    const pageId = attributes?.['pageId'];
    const navigationOption = attributes?.['navigationOption'];
    const externalUrl = attributes?.['externalUrl'];

    if (externalUrl && navigationOption === NavigationOptionEnum.USE_AS_EXTERNAL_LINK) {
      return null;
    }

    if (zone === 'login') {
      return null;
    }

    if (!zone || !role || !pageUrl) {
      errors.push(
        `Missing zone, role, or pageUrl attributes. zone: ${zone}, role: ${role}, pageUrl: ${pageUrl}, device: ${pageOverview.device}`,
      );
    }

    if (pageId) {
      const linkedPage = this.getPageConfigById(pageId, this.workflowConfig);
      if (!linkedPage) {
        errors.push(`Invalid pageId linked: ${pageId}`);
      }
    } else {
      const linkedPage = this.getPageConfigByUrlInZoneRoleDevice(
        pageUrl,
        zone,
        role === 'EmptyRole' ? pageOverview.role : role,
        pageOverview.device,
        this.workflowConfig,
      );
      if (!linkedPage) {
        errors.push(
          `Link does not work! Cannot find linked page: pageUrl: ${pageUrl}, zone: ${zone}, role: ${
            role === 'EmptyRole' ? 'EmptyRole so resolve: ' + pageOverview.role : role!
          }, device: ${pageOverview.device}, navigationOption: ${navigationOption}${
            navigationOption === NavigationOptionEnum.USE_AS_EXTERNAL_LINK
              ? `, externalUrl: ${externalUrl}`
              : ''
          }`,
        );
      }
    }

    if (errors.length > 0) {
      return {
        pageOverview: pageOverview,
        pageId: pageOverview.pageId,
        pageName: pageOverview.pageName,
        pageTitle: pageOverview.title,
        widgetName: widgetConfig.name,
        widgetId: widgetConfig.uniqueId.toString(),
        widgetDisplayName: widgetConfig.widgetConfiguration.widgetConfigDisplayName || '',
        role: pageOverview.role,
        zone: pageOverview.zone,
        device: pageOverview.device,
        url: pageOverview.fullUrl,
        errors: errors,
      };
    } else {
      return null;
    }
  }

  public findDuplicatePagesByZoneUrlSameRoleOrPageId() {
    const pageList = this.flattenPagesToList(this.workflowConfig);
    const duplicatesByZoneUrlAndRole: { [key: string]: PageOverview[] } = {};
    const duplicatesByPageId: { [key: string]: PageOverview[] } = {};
    const pagesWithNoPageId: { [key: string]: PageOverview } = {};
    const filteredDuplicatesByZoneUrlAndRole: { [key: string]: PageOverview[] } = {};
    const filteredDuplicatesByPageId: { [key: string]: PageOverview[] } = {};
    pageList.forEach((page) => {
      const zoneUrlAndRoleAndDeviceKey = `${page.fullUrl}||${page.role}||${page.device}`;
      if (!duplicatesByZoneUrlAndRole[zoneUrlAndRoleAndDeviceKey]) {
        duplicatesByZoneUrlAndRole[zoneUrlAndRoleAndDeviceKey] = [];
      }
      duplicatesByZoneUrlAndRole[zoneUrlAndRoleAndDeviceKey].push(page);

      if (!page.pageId) {
        pagesWithNoPageId[zoneUrlAndRoleAndDeviceKey] = page;
      } else {
        if (!duplicatesByPageId[page.pageId]) {
          duplicatesByPageId[page.pageId] = [];
        }
        duplicatesByPageId[page.pageId].push(page);
      }
    });

    Object.values(duplicatesByZoneUrlAndRole).forEach((pages) => {
      if (pages.length > 1) {
        // Keep only duplicates
        filteredDuplicatesByZoneUrlAndRole[pages[0].fullUrl] = pages;
      }
    });

    Object.values(duplicatesByPageId).forEach((pages) => {
      if (pages.length > 1) {
        // Keep only duplicates
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filteredDuplicatesByPageId[pages[0].pageId!] = pages;
      }
    });

    return { filteredDuplicatesByZoneUrlAndRole, filteredDuplicatesByPageId, pagesWithNoPageId };
  }

  public flattenPagesToList(workflowConfig: WorkflowConfig) {
    const pageList: PageOverview[] = [];
    workflowConfig.permissionSections.roles.forEach((role) => {
      Object.entries(role.zones).forEach(([zoneName, zone]) => {
        const canvas = zone?.canvases.subscription['default'];
        if (canvas) {
          const workflowZonesDevicesConfig = canvas as WorkflowZoneDevicesConfig;

          Object.entries(workflowZonesDevicesConfig).forEach(([device, deviceConfig]) => {
            const pages = deviceConfig?.pages;

            pages?.forEach((_page, pageIndex) => {
              const pageId = _page.pageUniqueId;
              const url = _page.pageConfiguration?.url;
              const title = _page.title;
              pageList.push({
                pageId: pageId,
                url: url,
                pageName: _page.name,
                title: title,
                fullUrl: `/zones/${formatNameAsUrl(zoneName)}/${url}`,
                role: role.slug,
                zone: formatNameAsUrl(zoneName),
                zoneFullName: zoneName,
                zonePageIndex: pageIndex,
                device: device,
                page: _page,
              });
            });
          });
        }
      });
    });
    return pageList;
  }

  public getUrlForPersonasToPageIds() {
    const deviceToPageMap: {
      [device: string]: {
        [role: string]: {
          [zone: string]: {
            pageId: string;
            url: string;
            pageName: string;
            title: string;
            fullUrl: string;
            role: string;
            zone: string;
            device: string;
          };
        };
      };
    } = {};

    const pageIdToUrlMap: {
      [role: string]: {
        [fullUrl: string]: {
          pageId: string;
          url: string;
          pageName: string;
          title: string;
          fullUrl: string;
          role: string;
          zone: string;
          device: string;
        };
      };
    } = {};

    this.workflowConfig.permissionSections.roles.forEach((role) => {
      Object.entries(role.zones).forEach(([zoneName, zone]) => {
        const canvas = zone?.canvases.subscription['default'];
        if (canvas) {
          const workflowZonesDevicesConfig = canvas as WorkflowZoneDevicesConfig;

          Object.entries(workflowZonesDevicesConfig).forEach(([device, deviceConfig]) => {
            const pages = deviceConfig?.pages;

            pages?.forEach((_page) => {
              const pageId = _page.pageUniqueId;
              const url = _page.pageConfiguration?.url;
              const title = _page.title;
              merge(deviceToPageMap, {
                [device]: {
                  [role.slug]: {
                    [`/zones/${formatNameAsUrl(zoneName)}/${url}`]: {
                      pageId: pageId,
                      url: url,
                      pageName: _page.name,
                      title: title,
                      fullUrl: `/zones/${formatNameAsUrl(zoneName)}/${url}`,
                      role: role.slug,
                      zone: formatNameAsUrl(zoneName),
                      device: device,
                    },
                  },
                },
              });
              merge(pageIdToUrlMap, {
                [role.slug]: {
                  [`/zones/${formatNameAsUrl(zoneName)}/${url}`]: {
                    pageId: pageId,
                    url: url,
                    pageName: _page.name,
                    title: title,
                    fullUrl: `/zones/${formatNameAsUrl(zoneName)}/${url}`,
                    role: role.slug,
                    zone: formatNameAsUrl(zoneName),
                    device: device,
                  },
                },
              });
            });
          });
        }
      });
    });
    return { deviceToPageMap, pageIdToUrlMap };
  }

  private setupActionDispatchListeners() {
    const droppedWidgetAction$ = this.actions$.pipe(ofActionDispatched(DroppedWidgetAction));
    const droppedWidgetIntoLayoutWidgetAction$ = this.actions$.pipe(
      ofActionDispatched(DroppedWidgetIntoLayoutWidgetAction),
    );

    let droppedWidgetActionSubscription = this.setupDroppedWidgetSubscription(droppedWidgetAction$);

    droppedWidgetIntoLayoutWidgetAction$.subscribe((action) => {
      if (this.draggedWidgetConfig) {
        droppedWidgetActionSubscription.unsubscribe(); // Unsubscribe from 'DroppedWidgetAction'
        let widgetConfig = JSON.parse(JSON.stringify(this.draggedWidgetConfig));
        if (widgetConfig.uniqueId !== widgetConfig.name) {
          widgetConfig = {
            ...widgetConfig,
            uniqueId: new UUID().toString(),
          };
        }
        this.store.dispatch(new NewWidgetEvent(widgetConfig.name, widgetConfig.uniqueId));
        this.addWidgetOnPage(
          widgetConfig,
          action.widgetDroppedInto.uniqueId,
          undefined,
          action?.target2dChildComponentIndex,
          action?.widgetLayoutType,
        );
        timer(1500).subscribe(() => {
          droppedWidgetActionSubscription = this.setupDroppedWidgetSubscription(droppedWidgetAction$);
        }); // Resubscribe after 1.5 seconds
      }
    });
  }

  private setupDroppedWidgetSubscription(droppedWidgetAction$: Observable<DroppedWidgetAction>) {
    return droppedWidgetAction$.subscribe((action) => {
      if (this.draggedWidgetConfig) {
        this.addWidgetToZoneAndPage(this.draggedWidgetConfig, action?.widgetLayoutType);
      }
    });
  }

  get quickLinkWitRoleWidgets(): WidgetNames[] {
    return Object.entries(WidgetStyleConst)
      .filter(([, value]) => value?.isShowNavigationToUrl === true)
      .map(([key]) => key as WidgetNames);
  }

  public overwritePage(page: WorkflowPageConfig, shouldNotOverwriteUrl = false) {
    const { activeDevice, activePageIndex, activeRole } = this.activeConfigService.activeState;
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zone = this.getZoneConfig(workflowConfig);
      let pageNavigationMap = workflowConfig?.pageNavigationMap;

      if (role) {
        const zoneDevice = zone[activeDevice];
        const oldPage = zoneDevice?.pages[activePageIndex];
        if (zoneDevice && oldPage) {
          pageNavigationMap = this.removeOldPageNavigationDataFromPageNavigationMapAndAddNewData(
            pageNavigationMap,
            oldPage,
            page,
            this.activeConfig.activeDevice,
          );
          if (oldPage.pageUniqueId && pageNavigationMap?.[activeDevice]?.[oldPage.pageUniqueId]) {
            delete pageNavigationMap[activeDevice][oldPage.pageUniqueId];
          }
          zoneDevice.pages[activePageIndex] = {
            ...page,
            pageConfiguration: {
              ...page.pageConfiguration,
              url: shouldNotOverwriteUrl ? oldPage.pageConfiguration.url : page.pageConfiguration.url,
            },
            name: shouldNotOverwriteUrl ? oldPage.name : page.name,
            title: shouldNotOverwriteUrl ? oldPage.title : page.title,
          };

          this.forAllWidgetsOfTypeInPage(
            this.quickLinkWitRoleWidgets,
            zoneDevice.pages[activePageIndex],
            (config) => {
              if (config.widgetConfiguration.attributes['role']) {
                config.widgetConfiguration.attributes['role'] = formatUrl(this.activeConfig.activeRole);
              }
              return config;
            },
          );
        }
      }
      workflowConfig['pageNavigationMap'] = pageNavigationMap;
      return workflowConfig;
    });
  }

  public overwriteWidget(
    widgetConfig: NewWorkflowWidgetConfig,
    widgetUniqueId: string,
    widgetLayoutType?: LayoutNames,
  ) {
    const { activeDevice, activePageIndex, activeRole } = this.activeConfigService.activeState;
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zone = this.getZoneConfig(workflowConfig);

      if (role) {
        const zoneDevice = zone[activeDevice];
        const oldPage = zoneDevice?.pages[activePageIndex];
        if (zoneDevice && oldPage && zoneDevice?.pages?.[activePageIndex]?.widgets) {
          this.forAllWidgetsOfTypeInPage(
            this.quickLinkWitRoleWidgets,
            zoneDevice.pages[activePageIndex],
            (config) => {
              if (config.widgetConfiguration.attributes['role']) {
                config.widgetConfiguration.attributes['role'] = formatUrl(this.activeConfig.activeRole);
              }
              return config;
            },
          );
          const pageConfig = zoneDevice?.pages[activePageIndex];
          const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType, widgetUniqueId);
          findAndReplaceWidget(widgetUniqueId, widgets, widgetConfig);
        }
      }

      return workflowConfig;
    });
  }

  public getActiveZone(): CanvasZone | null | undefined {
    return this.workflowConfig.permissionSections.roles.find(
      (role) => role.name === this.activeConfig.activeRole,
    )?.zones[this.activeConfig.activeZone];
  }

  public getActivePage(): WorkflowPageConfig | undefined {
    return this.getZoneConfig(this.workflowConfig)[this.activeConfig.activeDevice]?.pages[
      this.activeConfig.activePageIndex
    ];
  }

  public getRoleConfig(roleSlug: string): Role | undefined {
    return JSON.parse(
      JSON.stringify(this.workflowConfig.permissionSections.roles.find((role) => role.slug === roleSlug)),
    );
  }
  public overwriteZoneDevice(
    zoneDeviceConfig: WorkflowZoneConfig,
    device: SupportedDevice,
    zone: string = this.activeConfig.activeZone,
    targetPersona: string = formatNameAsUrl(this.activeConfig.activeRole),
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const configToUpdate = workflowConfig;

      const roleExistIndex = configToUpdate.permissionSections.roles.findIndex(
        (role) => role.slug === targetPersona,
      );

      this.rewriteQuickLinkRolesInZoneDevice(zoneDeviceConfig, targetPersona);

      if (roleExistIndex !== -1) {
        const config =
          configToUpdate.permissionSections.roles[roleExistIndex].zones[zone]?.canvases.subscription[
            'default'
          ];
        if (config) config[device] = zoneDeviceConfig;
      }
      return configToUpdate;
    });
  }

  public overwriteZone(
    zoneConfig: CanvasZone,
    zone: string = this.activeConfig.activeZone,
    targetPersona: string = formatNameAsUrl(this.activeConfig.activeRole),
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const configToUpdate = workflowConfig;

      const roleExistIndex = configToUpdate.permissionSections.roles.findIndex(
        (role) => role.slug === targetPersona,
      );

      this.rewriteQuickLinkRoles(zoneConfig, targetPersona);

      if (roleExistIndex !== -1) {
        configToUpdate.permissionSections.roles[roleExistIndex].zones[zone] = zoneConfig;
      }
      return configToUpdate;
    });
  }

  // target persona should be null if you don't intend to overwrite another persona
  public addRoleConfig(
    roleConfig: Role,
    targetPersona: string | null = null,
    workflowConfig: WorkflowConfig | undefined = undefined,
  ): WorkflowConfig {
    const configToUpdate = workflowConfig ?? this.workflowConfig;

    const personaSlug = targetPersona ? targetPersona : roleConfig.slug;
    const roleExistIndex = configToUpdate.permissionSections.roles.findIndex(
      (role) => role.slug === personaSlug,
    );

    Object.entries(roleConfig.zones).forEach(([_, canvases]) => {
      if (canvases) this.rewriteQuickLinkRoles(canvases, roleConfig.slug);
    });

    if (roleExistIndex !== -1) {
      configToUpdate.permissionSections.roles[roleExistIndex] = {
        ...roleConfig,
        id: targetPersona ? configToUpdate.permissionSections.roles[roleExistIndex].id : roleConfig.id,
        name: targetPersona ? configToUpdate.permissionSections.roles[roleExistIndex].name : roleConfig.name,
        slug: targetPersona ? configToUpdate.permissionSections.roles[roleExistIndex].slug : roleConfig.slug,
        userType: targetPersona
          ? configToUpdate.permissionSections.roles[roleExistIndex].userType
          : roleConfig.userType,
      };
    } else {
      configToUpdate.permissionSections.roles.push(roleConfig);
    }
    return configToUpdate;
  }

  private rewriteQuickLinkRolesInZoneDevice(zoneDeviceConfig: WorkflowZoneConfig, roleSlug: string) {
    this.forAllWidgetsOfTypeInZoneDevice(this.quickLinkWitRoleWidgets, zoneDeviceConfig, (config) => {
      if (config.widgetConfiguration.attributes['role']) {
        config.widgetConfiguration.attributes['role'] = roleSlug;
      }
      return config;
    });
  }

  private rewriteQuickLinkRoles(zoneConfig: CanvasZone, roleSlug: string) {
    this.forAllWidgetsOfTypeInZone(this.quickLinkWitRoleWidgets, zoneConfig, (config) => {
      if (config.widgetConfiguration.attributes['role']) {
        config.widgetConfiguration.attributes['role'] = roleSlug;
      }
      return config;
    });
  }

  addPageTemplateToCurrentPersona(
    templates: PageTemplateDto[] | PageTemplateDto,
    breakpoints: ('mobile' | 'tablet' | 'desktop')[] = ['mobile', 'tablet', 'desktop'],
    useActiveZone = false,
  ) {
    const templatesArray = Array.isArray(templates) ? templates : [templates];

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const persona = this.getActiveRoleConfig(workflowConfig);
      let pageNavigationMap = workflowConfig?.pageNavigationMap;

      templatesArray.forEach((template) => {
        const zone = useActiveZone ? this.activeConfig.activeZone : (template.zoneKey as APP_ZONES);
        const devicesConfig = persona?.zones[zone]?.canvases.subscription[
          'default'
        ] as WorkflowZoneDevicesConfig;

        const defaultConfig = {
          zoneTypeConfig: 'new' as const,
          zoneConfiguration: {
            zonePageType: 'singlePage',
            zoneType: ZONETYPEOPTION.DEFAULT,
          },
          pages: [],
        } satisfies WorkflowZoneConfig;

        // Initialize missing device configs
        breakpoints.forEach((device) => {
          if (!devicesConfig[device]) {
            try {
              devicesConfig[device] = { ...defaultConfig };
            } catch (e) {
              console.error(e);
              this.alertHandlerService.showAlertFn(
                AlertTypes.WARNING,
                `Unable to import page template at persona: ${persona?.name}, zone: ${zone}, on device: ${device}. Does the zone exist in this persona?`,
              );
            }
          }
        });

        breakpoints.forEach((device) => {
          const pages = devicesConfig?.[device]?.pages ?? [];
          const pageIndex = pages.findIndex(
            (page) =>
              page &&
              typeof page === 'object' &&
              page.pageConfiguration &&
              page.pageConfiguration.url === template.pageKey,
          );
          const pageConfigForDevice = template?.devicesPageConfig?.[device];
          // don't add empty page configs
          if (
            pageConfigForDevice &&
            typeof pageConfigForDevice === 'object' &&
            Object.keys(pageConfigForDevice).length > 0
          ) {
            if (pageIndex !== -1) {
              pageNavigationMap = this.removeOldPageNavigationDataFromPageNavigationMapAndAddNewData(
                pageNavigationMap,
                pages[pageIndex],
                pageConfigForDevice,
                device,
              );
              pages.splice(pageIndex, 1, pageConfigForDevice);
            } else {
              pages.push(pageConfigForDevice);
              pageNavigationMap = this.addANewEntryOnPageNavigationMap(
                pageNavigationMap,
                pageConfigForDevice,
                device,
              );
            }
          }
        });
      });
      workflowConfig['pageNavigationMap'] = pageNavigationMap;
      return workflowConfig;
    });
  }

  private addANewEntryOnPageNavigationMap(
    pageNavigationMap: PageNavigationMap | undefined,
    pageConfig: WorkflowPageConfig,
    device: SupportedDevice,
  ) {
    pageNavigationMap ??= {};
    pageNavigationMap[device] ??= {};
    pageConfig['pageUniqueId'] ??= new UUID().toString();
    pageNavigationMap[device] = {
      ...pageNavigationMap[device],
      [pageConfig.pageUniqueId]: this.pageNavigationMapService.createPageNavigationMetadataForPage(
        pageConfig,
        this.activeConfig,
      ),
    };
    return pageNavigationMap;
  }

  private removeOldPageNavigationDataFromPageNavigationMapAndAddNewData(
    pageNavigationMap: PageNavigationMap | undefined,
    oldPageConfig: WorkflowPageConfig,
    newPageConfig: WorkflowPageConfig,
    device: SupportedDevice,
  ) {
    pageNavigationMap ??= {};
    pageNavigationMap[device] ??= {};
    if (oldPageConfig?.pageUniqueId && pageNavigationMap[device]?.[oldPageConfig.pageUniqueId]) {
      delete pageNavigationMap[device][oldPageConfig.pageUniqueId];
    }
    newPageConfig['pageUniqueId'] ??= new UUID().toString();
    pageNavigationMap[device] = {
      ...pageNavigationMap[device],
      [newPageConfig.pageUniqueId]: this.pageNavigationMapService.createPageNavigationMetadataForPage(
        newPageConfig,
        this.activeConfig,
      ),
    };
    return pageNavigationMap;
  }

  private forAllWidgetsOfTypeInPage(
    types: WidgetNames[],
    page: WorkflowPageConfig,
    fn: (config: WorkflowWidgetConfig) => WorkflowWidgetConfig,
  ) {
    page.widgets.forEach((widget) => {
      if (types.includes(widget.name as WidgetNames)) {
        fn(widget);
      }
    });
  }

  public setWidgetsOnActivePage(newWidgets: WorkflowWidgetConfig[]) {
    const page = this.getActivePage();
    if (page) {
      page.widgets = newWidgets;
      this.overwritePage(page);
    }
  }

  private forAllWidgetsOfTypeInZone(
    types: WidgetNames[],
    zone: CanvasZone,
    fn: (config: WorkflowWidgetConfig) => WorkflowWidgetConfig,
  ) {
    const subscription = zone?.canvases.subscription as {
      [key: string]: WorkflowZoneDevicesConfig;
    };
    Object.entries(subscription).forEach(([_, deviceConfig]) => {
      const devices = ['mobile', 'tablet', 'desktop'];
      devices.forEach((device) => {
        const zoneDevice = (deviceConfig as any)[device] as WorkflowZoneConfig | undefined;
        if (zoneDevice) this.forAllWidgetsOfTypeInZoneDevice(types, zoneDevice, fn);
      });
    });
  }

  private forAllWidgetsOfTypeInZoneDevice(
    types: WidgetNames[],
    zoneDevice: WorkflowZoneConfig,
    fn: (config: WorkflowWidgetConfig) => WorkflowWidgetConfig,
  ) {
    zoneDevice?.pages.forEach((page) => {
      page.widgets.forEach((widget) => {
        if (types.includes(widget.name as WidgetNames)) {
          fn(widget);
        }
      });
    });
  }

  public addWidgetToZoneAndPage(
    newWidgetConfig: WorkflowWidgetConfig,
    widgetLayoutType?: LayoutNames | string | undefined,
  ) {
    // Deep copy the widget config to make sure references don't get mixed up
    let widgetConfig = JSON.parse(JSON.stringify(newWidgetConfig)) as WorkflowWidgetConfig;
    // if not a widget that only allows one unique id then regenerate id
    if (widgetConfig.uniqueId !== widgetConfig.name) {
      widgetConfig = {
        ...widgetConfig,
        uniqueId: new UUID().toString(),
      };
    }

    updateChildComponentUniqueIds(widgetConfig.childComponents);

    // --- Begin: Compatibility check ---

    const zoneConfig = this.getZoneConfig(this.workflowConfig);
    const pageConfig = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
    const pageWidgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType);
    if (pageWidgets) this.checkWidgetCompatibility(widgetConfig.name, pageWidgets);
    // --- End: Compatibility check ---

    this.store.dispatch(new NewWidgetEvent(widgetConfig.name, widgetConfig.uniqueId));
    const { activeDevice, activePageIndex, activeZone, activeRole } = this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zone = this.getZoneConfig(workflowConfig);

      if (role) {
        let page = zone[activeDevice]?.pages[activePageIndex];

        if (zone[activeDevice] && !page) {
          const baasTags = this.store.selectSnapshot(ProjectsState.getActiveProject).relevantBaasTags;

          createPage(
            activeZone,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            zone[activeDevice]!,
            activePageIndex,
            'dynamicGrid',
            baasTags,
          );
          page = zone[activeDevice]?.pages[activePageIndex];
        }

        // check if popupType is available, display the popup requested and return workflowConfig without updating
        if (widgetConfig?.popupType && zone[activeDevice]) {
          const isPopupAvailable = this.checkForPopupTypeAndDispatchAction(widgetConfig?.popupType);
          if (isPopupAvailable) return workflowConfig;
        }

        /**
         * @description: to add default global styling while dropping the widgets
         */

        widgetConfig = this.applyDefaultGlobalStyling(widgetConfig);

        // if addWidgetToPageOrAlternativeWidgets returns false then widget should not be addedso exit
        if (
          // This is fine as createPage will create the pages
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          !this.addWidgetToPageOrAlternativeWidgets(widgetConfig, zone, page!, widgetLayoutType)
        ) {
          return workflowConfig;
        }

        this.executeZoneHook(widgetConfig, activePageIndex, zone, 'add');

        this.executeRoleHook(widgetConfig, role, zone, 'add');
      }

      return workflowConfig;
    });
  }

  private checkForPopupTypeAndDispatchAction(popupType: WORKFLOW_POPUPS | null | undefined): boolean {
    if (popupType) {
      this.store.dispatch(new DisplayPopupAction(popupType));
    }
    return !!popupType;
  }

  updateWidgetAttributes(widgetUniqueId: string | number, attributesUpdate: any) {
    const { activeDevice, activePageIndex, activeRole } = this.activeConfigService.activeState;
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = this.getZoneConfig(workflowConfig);

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(widgetUniqueId, widgets);
        if (widget) {
          widget.widgetConfiguration.attributes = {
            ...widget.widgetConfiguration.attributes,
            ...attributesUpdate,
          };
        }
      }

      return workflowConfig;
    });
  }

  updateWidgetProperties(widgetUniqueId: string | number, propertiesUpdate: any) {
    const { activeDevice, activePageIndex, activeRole } = this.activeConfigService.activeState;
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = this.getZoneConfig(workflowConfig);

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(widgetUniqueId, widgets);
        if (widget) {
          widget.widgetConfiguration.attributes.widgetProperties = {
            ...widget.widgetConfiguration.attributes.widgetProperties,
            ...propertiesUpdate,
          };
        }
      }

      return workflowConfig;
    });
  }

  removeSpaceAddHyphen = (name: string) => {
    try {
      return name.toLowerCase().replace(/\s/g, '-');
    } catch (error) {
      return '';
    }
  };

  public isCustomQuestionExist(questionUniqueId: string) {
    let isQuestionExist = false;
    this.workflowConfigService
      .getConfig()
      .pipe(
        take(1),
        map((workflowConfig) => {
          const formattedQuestionUniqueId = this.removeSpaceAddHyphen(questionUniqueId);
          if (workflowConfig.customData.questions[formattedQuestionUniqueId]?.questionUniqueId) {
            isQuestionExist = true;
            this.alertHandlerService.showAlertFn('error', 'Custom Question already exists.');
          }
        }),
      )
      .subscribe();
    return isQuestionExist;
  }

  addCustomData(attributesUpdate: WorkflowWidgetConfig, customDataQuestionData: customDataQuestionDataType) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const attributes = attributesUpdate.widgetConfiguration.attributes;
      const questionProperties = attributes['questionProperties'];
      const questionUniqueId = attributes['questionUniqueId'];
      const formattedQuestionUniqueId = this.removeSpaceAddHyphen(questionUniqueId);

      workflowConfig = this.onModifyCustomQuestionData({
        attributes,
        questionProperties,
        customDataQuestionData,
        formattedQuestionUniqueId,
        questionUniqueId,
        workflowConfig,
      });

      return workflowConfig;
    });
  }

  private onModifyCustomQuestionData(arg: {
    workflowConfig: WorkflowConfig;
    formattedQuestionUniqueId: string;
    questionUniqueId: string;
    questionProperties: questionProperties;
    attributes: WorkflowWidgetConfigurationAttributes;
    customDataQuestionData: customDataQuestionDataType;
  }) {
    arg.questionProperties.name = arg.formattedQuestionUniqueId;
    arg.workflowConfig = {
      ...arg.workflowConfig,
      customData: {
        ...arg.workflowConfig?.customData,
        questions: {
          ...arg.workflowConfig?.customData?.questions,
          [arg.formattedQuestionUniqueId]: {
            ...arg.workflowConfig?.customData?.questions?.[arg.formattedQuestionUniqueId],
            questionUniqueId: arg.formattedQuestionUniqueId,
            type: arg.questionProperties['type'],
            header: arg.attributes['label'],
            subHeader: arg.attributes['sub_heading'],
            questionProperties: arg.questionProperties,
            validationRules: arg.attributes?.['validationRules'] ?? [],
          },
        },
        data: {
          ...arg.workflowConfig?.customData?.data,
          [arg.formattedQuestionUniqueId]: {
            ...arg.workflowConfig?.customData?.data?.[arg.formattedQuestionUniqueId],
            data_type: arg.customDataQuestionData.data_type, // datetime, string, number, stringArray
            entity: arg.customDataQuestionData.entity, // user_preference
            sink: arg.customDataQuestionData.sink, // metadata || hasura
          },
        },
      },
    };
    return arg.workflowConfig;
  }

  updateCustomDataFromPopup(
    questionUniqueId: string,
    attributesUpdate: WorkflowWidgetConfig,
    customDataQuestionData: customDataQuestionDataType,
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const attributes = attributesUpdate.widgetConfiguration.attributes;
      const questionProperties = attributes['questionProperties'];
      const formattedQuestionUniqueId = this.removeSpaceAddHyphen(questionUniqueId);

      workflowConfig = this.onModifyCustomQuestionData({
        attributes,
        questionProperties,
        customDataQuestionData,
        formattedQuestionUniqueId,
        questionUniqueId,
        workflowConfig,
      });
      return workflowConfig;
    });
  }

  updateCustomData(customQuestionUniqueId: string, customQuestionData: customDataQuestionType) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const questionProperties = customQuestionData['questionProperties'];
      const formattedQuestionUniqueId = this.removeSpaceAddHyphen(customQuestionUniqueId);
      questionProperties.name = formattedQuestionUniqueId;

      workflowConfig = {
        ...workflowConfig,
        customData: {
          ...workflowConfig?.customData,
          questions: {
            ...workflowConfig?.customData?.questions,
            [formattedQuestionUniqueId]: {
              ...workflowConfig?.customData?.questions[formattedQuestionUniqueId],
              questionUniqueId: formattedQuestionUniqueId,
              type: questionProperties['type'],
              header: customQuestionData['header'],
              subHeader: customQuestionData['subHeader'],
              questionProperties: questionProperties,
              validationRules: customQuestionData?.['validationRules'] ?? [],
            },
          },
        },
      };
      return workflowConfig;
    });
  }

  addTabToWidget(uniqueId: string) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          const tabConfigs = widget.widgetConfiguration.attributes.widgetProperties?.['tabConfigs'] as
            object[] | undefined;
          if (tabConfigs) {
            tabConfigs.push({
              title: `Tab ${tabConfigs.length + 1}`,
              tabRef: `tab${tabConfigs.length + 1}`,
              outerCssConfig: '',
            });
            (widget.childComponents as NewWorkflowWidgetConfig[][]).push([]);
          }

          const multiColumnConfig =
            widget.widgetConfiguration.attributes.widgetProperties?.['selectedNoOfColumn'];
          if (multiColumnConfig) {
            const currentColumnCount = (widget.childComponents as NewWorkflowWidgetConfig[][])?.length;

            // If the current number of child components is less than selectedNoOfColumn, push empty arrays
            if (currentColumnCount < multiColumnConfig) {
              const missingColumns = multiColumnConfig - currentColumnCount;
              for (let i = 0; i < missingColumns; i++) {
                (widget.childComponents as NewWorkflowWidgetConfig[][]).push([]);
              }
            }

            // If the current number of child components is greater than selectedNoOfColumn, trim the excess arrays
            if (currentColumnCount > multiColumnConfig) {
              const excessColumns = currentColumnCount - multiColumnConfig;
              (widget.childComponents as NewWorkflowWidgetConfig[][]).splice(
                multiColumnConfig,
                excessColumns,
              );
            }
          }
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to add tab');
        }
      }

      return workflowConfig;
    });
  }

  removeTabFromWidget(uniqueId: string, column: number) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          (widget.childComponents as NewWorkflowWidgetConfig[][]).splice(column, 1);
          widget.widgetConfiguration.attributes.widgetProperties?.['tabConfigs'].splice(column, 1);
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to delete');
        }
      }

      return workflowConfig;
    });
  }

  /**
   * Removes a single column from a multi column widget along with the widgets it holds.
   */
  public removeColumnFromWidget(
    layoutWidgetUniqueId: string | number,
    columnIndex: number,
    widgetLayoutType?: LayoutNames,
  ) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;
      const pageConfig = zoneConfig?.[activeDevice]?.pages?.[activePageIndex];

      const columnCountChange = removeColumnFromMultiColumnWidget(
        pageConfig,
        layoutWidgetUniqueId,
        columnIndex,
        widgetLayoutType,
        this.alertHandlerService,
      );

      if (columnCountChange) {
        this.columnCountChangeState.set(columnCountChange);
      }

      return workflowConfig;
    });
  }

  /**
   * Clears the pending column count change once the RHS config has applied it, so a later instance of
   * that config cannot re-apply a snapshot that has since been superseded.
   */
  public consumeColumnCountChange(): void {
    this.columnCountChangeState.set(null);
  }

  public setAccordionItemCount(uniqueId: string, newCount: number) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        const widgets = zoneConfig?.[activeDevice]?.pages?.[activePageIndex]?.widgets;
        if (!widgets?.length) {
          return workflowConfig;
        }
        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          const accordionConfigs = widget.widgetConfiguration.attributes.widgetProperties?.[
            'accordionConfigs'
          ] as object[] | undefined;
          if (accordionConfigs) {
            /**
             * If the new count is greater than the current count, add new accordion items with default values. If the new count is less than the current count, remove the excess accordion items.
             */
            const diff = newCount - accordionConfigs.length;
            if (diff > 0) {
              for (let i = 0; i < diff; i++) {
                const nextIndex = accordionConfigs.length + 1;
                accordionConfigs.push({
                  title: `Accordion ${nextIndex}`,
                  containerName: `Accordion: ${nextIndex}`,
                  css: '',
                  defaultState: AccordionDefaultState.COLLAPSED,
                });
                (widget.childComponents as NewWorkflowWidgetConfig[][]).push([]);
              }
            } else if (diff < 0) {
              accordionConfigs.splice(newCount);
              (widget.childComponents as NewWorkflowWidgetConfig[][]).splice(newCount);
            }
          }
        } else {
          this.alertHandlerService.showAlertFn(
            AlertTypes.WARNING,
            'No widget found to update accordion count',
          );
        }
      }

      return workflowConfig;
    });
  }

  public addAccordionItemToWidget(uniqueId: string) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;
        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          const accordionConfigs = widget.widgetConfiguration.attributes.widgetProperties?.[
            'accordionConfigs'
          ] as object[] | undefined;
          if (accordionConfigs) {
            const nextIndex = accordionConfigs.length + 1;
            accordionConfigs.push({
              title: `Accordion ${nextIndex}`,
              containerName: `Accordion: ${nextIndex}`,
              css: '',
              defaultState: AccordionDefaultState.COLLAPSED,
            });
            (widget.childComponents as NewWorkflowWidgetConfig[][]).push([]);
          }
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to add accordion item');
        }
      }

      return workflowConfig;
    });
  }

  public removeAccordionItemFromWidget(uniqueId: string, index: number) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;
        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          (widget.childComponents as NewWorkflowWidgetConfig[][]).splice(index, 1);
          widget.widgetConfiguration.attributes.widgetProperties?.['accordionConfigs'].splice(index, 1);
        } else {
          this.alertHandlerService.showAlertFn(
            AlertTypes.WARNING,
            'No widget found to delete accordion item',
          );
        }
      }

      return workflowConfig;
    });
  }

  public syncChildComponentCount(
    uniqueId: string,
    configPropertyName: 'selectedNoOfColumn' | 'numberOfSlides',
  ) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (!role) return workflowConfig;

      const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

      const widget = findWidget(uniqueId, widgets);

      if (!widget) {
        this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found');
        return workflowConfig;
      }

      this.updateChildComponentCount(widget, configPropertyName);

      return workflowConfig;
    });
  }

  private updateChildComponentCount(
    widget: WorkflowWidgetConfig,
    configPropertyName: 'selectedNoOfColumn' | 'numberOfSlides',
  ) {
    // Read the target child component count from the widget property (selectedNoOfColumn or selectedNoOfSlides)
    const targetCount = widget.widgetConfiguration.attributes.widgetProperties?.[configPropertyName];

    if (!targetCount) return;

    const currentCount = (widget.childComponents as NewWorkflowWidgetConfig[][])?.length;
    // If the current number of child components is less than the target count, add the missing empty arrays
    if (currentCount < targetCount) {
      const missing = targetCount - currentCount;
      for (let i = 0; i < missing; i++) {
        (widget.childComponents as NewWorkflowWidgetConfig[][]).push([]);
      }
    }

    // If the current number of child components is greater than the target count, remove the excess arrays from the end
    if (currentCount > targetCount) {
      const excessColumns = currentCount - targetCount;
      (widget.childComponents as NewWorkflowWidgetConfig[][]).splice(targetCount, excessColumns);
    }
  }

  public syncColumn(uniqueId: string) {
    this.syncChildComponentCount(uniqueId, 'selectedNoOfColumn');
  }

  public syncCarouselSlides(uniqueId: string) {
    this.syncChildComponentCount(uniqueId, 'numberOfSlides');
  }

  public setPendingWidgetDropContext(context: FormWidgetPlacementContext): void {
    this.pendingWidgetDropContext = context;
  }

  public consumePendingWidgetDropContext(): FormWidgetPlacementContext | null {
    const context = this.pendingWidgetDropContext;
    this.pendingWidgetDropContext = null;
    return context;
  }

  public clearPendingWidgetDropContext(): void {
    this.pendingWidgetDropContext = null;
  }

  addWidgetOnPage(
    widgetToAdd: NewWorkflowWidgetConfig,
    targetParentId: string | number | typeof TOP_LEVEL_ID,
    targetIndex?: number,
    target2dChildComponentIndex?: number,
    widgetLayoutType?: LayoutNames,
    zone: string = this.activeConfigService.activeState.activeZone,
    pageIndex: number = this.activeConfigService.activeState.activePageIndex,
  ) {
    const { activeDevice, activeRole, activeCanvasSubscription } = this.activeConfigService.activeState;
    //TODO: Can make this into a reusable function for moveWidgetOnPage as well
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[zone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        const pageConfig = zoneConfig[activeDevice]?.pages[pageIndex];
        const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType);

        try {
          // Do not update the unique ID if the widget is one of the following:
          //  IMAGE_WIDGET
          const widgetsToRetainUniqueId = [WidgetNames.IMAGE_WIDGET.toString()];
          const shouldRetainUniqueId = widgetsToRetainUniqueId.includes(widgetToAdd.name);

          if (widgetToAdd?.popupType) {
            this.setPendingWidgetDropContext({
              targetParentId,
              targetIndex,
              target2dChildComponentIndex,
              widgetLayoutType,
              zone,
              pageIndex,
            });
            const isPopupAvailable = this.checkForPopupTypeAndDispatchAction(widgetToAdd?.popupType);
            if (isPopupAvailable) return workflowConfig;
          }

          addWidget(
            widgetToAdd,
            targetParentId,
            widgets,
            targetIndex,
            target2dChildComponentIndex,
            shouldRetainUniqueId,
          );
        } catch (err) {
          if (err instanceof NotLayoutWidgetErr) {
            this.alertHandlerService.showAlertFn(
              AlertTypes.WARNING,
              'Cannot add widget below non layout widget',
            );
          } else {
            throw err;
          }
        }
      }

      return workflowConfig;
    });
  }

  moveWidgetOnPage(
    widgetId: string | number,
    targetParentId: string | number | typeof TOP_LEVEL_ID,
    targetIndex?: number,
    target2dChildComponentIndex?: number,
    newParentNodeData?: { widgetLayoutType: LayoutNames } | undefined,
    removeNodeData?: { widgetLayoutType: LayoutNames } | undefined,
    zone: string = this.activeConfigService.activeState.activeZone,
    pageIndex: number = this.activeConfigService.activeState.activePageIndex,
  ) {
    const { activeDevice, activeRole, activeCanvasSubscription } = this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[zone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        const pageConfig = zoneConfig[activeDevice]?.pages[pageIndex];

        // Determine the target array where the widget should be added or moved.
        // If `newParentNodeData` is provided, it uses the parent layout type to find the correct widget array.
        const targetArray = getWidgetsArrayByLayoutTypeOrWidgetId(
          pageConfig,
          newParentNodeData?.widgetLayoutType,
          targetParentId,
        );

        // Determine the source array from which the widget should be removed, if `removeNodeData` is provided.
        // This ensures the widget is properly removed from its current parent container before moving it.
        const removeWidgetFromArray = removeNodeData
          ? getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, removeNodeData?.widgetLayoutType, widgetId)
          : undefined;

        moveWidget(
          widgetId,
          targetParentId,
          targetArray,
          targetIndex,
          target2dChildComponentIndex,
          removeWidgetFromArray,
        );
      }

      return workflowConfig;
    });
  }

  /**
   * Retrieves the active role configuration based on the current active role index.
   * @param workflowConfig - The workflow configuration containing permission sections and roles.
   * @returns The active role configuration or undefined if it cannot be determined.
   */
  private getActiveRoleConfig(workflowConfig: WorkflowConfig): Role | undefined {
    return workflowConfig?.permissionSections?.roles?.[this.activeConfig.activeRoleIndex];
  }

  /**
   * Retrieves the active zone configuration based on the current active role and zone indices.
   * @param role - The active role configuration.
   * @returns The active zone configuration or undefined if it cannot be determined.
   */
  private getActiveZoneConfig(role: Role | undefined): WorkflowZoneDevicesConfig | undefined {
    return role?.zones?.[this.activeConfig.activeZone]?.canvases?.subscription?.[
      this.activeConfig.activeCanvasSubscription
    ] as WorkflowZoneDevicesConfig;
  }

  /**
   * Retrieves the active page configuration based on the current active device and page indices.
   * @param zone - The active zone configuration.
   * @returns The active page configuration or undefined if it cannot be determined.
   */
  private getActivePageConfig(zone: WorkflowZoneDevicesConfig | undefined): WorkflowPageConfig | undefined {
    return zone?.[this.activeConfig.activeDevice]?.pages?.[
      this.activeConfig.activePageIndex
    ] as WorkflowPageConfig;
  }

  /**
   * Updates the container name of a widget into containerConfig property.
   * @param layoutWidgetUniqueId - Unique identifier for the layout widget.
   * @param widgetIndex - Index of the widget to be renamed.
   * @param containerName - The new container name to set for the widget.
   */
  renameLayoutWidgetContainerName(
    layoutWidgetUniqueId: number | string,
    widgetIndex: number,
    containerName: string,
    widgetLayoutType?: LayoutNames,
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const roleConfig = this.getActiveRoleConfig(workflowConfig);
      const zoneConfig = this.getActiveZoneConfig(roleConfig);
      const pageConfig = this.getActivePageConfig(zoneConfig);

      if (pageConfig) {
        const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(
          pageConfig,
          widgetLayoutType,
          layoutWidgetUniqueId,
        );
        const widgetConfig = findWidget(layoutWidgetUniqueId, widgets);
        if (!widgetConfig) return workflowConfig;

        const totalContainerLength = widgetConfig?.childComponents?.length ?? 1;

        const widgetProperties = widgetConfig.widgetConfiguration.attributes?.widgetProperties ?? {};

        const widgetName = widgetConfig?.name as WidgetNames;
        const configPropertyName = containerConfigName[widgetName] as string;

        const containerConfig = addDefaultContainerConfigData(
          widgetProperties?.[configPropertyName],
          widgetConfig.widgetConfiguration.attributes?.['childComponentOuterCss'],
          widgetIndex,
          totalContainerLength,
          widgetName,
        );

        /**
         *  set the {@link containerName} into {@link containerConfig} with {@link widgetIndex}
         */
        containerConfig[widgetIndex] = {
          ...containerConfig[widgetIndex],
          containerName,
        };

        widgetConfig.widgetConfiguration.attributes.widgetProperties = {
          ...widgetProperties,
          [configPropertyName]: containerConfig,
        };
      }

      return workflowConfig;
    });
  }

  public renameWidgetDisplayName(
    widgetUniqueId: number | string,
    updatedDisplayName: string,
    widgetLayoutType: LayoutNames,
  ) {
    this.workflowConfigService.updateConfig((workflowConfig) => {
      const roleConfig = this.getActiveRoleConfig(workflowConfig);
      const zoneConfig = this.getActiveZoneConfig(roleConfig);
      const pageConfig = this.getActivePageConfig(zoneConfig);

      if (pageConfig) {
        const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType, widgetUniqueId);
        const widgetConfig = findWidget(widgetUniqueId, widgets);
        if (widgetConfig) widgetConfig.widgetConfiguration.widgetConfigDisplayName = updatedDisplayName;
      }

      return workflowConfig;
    });
  }

  /**
   * Retrieves the display name of a widget from the layout configuration.
   * @param layoutWidgetUniqueId - Unique identifier for the layout widget.
   * @param widgetIndex - Index of the widget for which to retrieve the display name.
   * @returns An Observable emitting the widget's display name or null if not found.
   */
  private getLayoutWidgetContainerName(
    layoutWidgetUniqueId: number | string,
    widgetIndex: string | number,
  ): Observable<string | null> {
    return this.workflowConfigService.getConfig().pipe(
      take(1),
      map((workflowConfig) => {
        const roleConfig = this.getActiveRoleConfig(workflowConfig);
        const zoneConfig = this.getActiveZoneConfig(roleConfig);
        const pageConfig = this.getActivePageConfig(zoneConfig);

        if (pageConfig) {
          const widgetConfig = findWidget(layoutWidgetUniqueId, pageConfig.widgets);
          const widgetName = widgetConfig?.name as WidgetNames;
          const configPropertyName = containerConfigName[widgetName] as string;

          /**
           *  get the @property {containerName} from {@link configPropertyName} with {@link widgetIndex}
           */
          return (
            widgetConfig?.widgetConfiguration?.attributes?.widgetProperties?.[configPropertyName]?.[
              widgetIndex
            ]?.['containerName'] || null
          );
        }
        return null;
      }),
    );
  }

  /**
   *  @returns Signal<string | null | undefined>
   *  It can be used like below.
   *  const displayNameSignal = this.workflowEditorService.layoutWidgetDisplayNameSignal(
   parentId,
   widgetIndex,
   );
   * @description: toSignal() can only be used within an injection context such as a constructor, a factory function, a field initializer, or a function used with runInInjectionContext.
   */
  layoutWidgetContainerNameSignal(
    layoutWidgetUniqueId: number | string,
    widgetIndex: string | number,
  ): Signal<string | null | undefined> {
    return runInInjectionContext(this.injector, () =>
      toSignal(this.getLayoutWidgetContainerName(layoutWidgetUniqueId, widgetIndex)),
    );
  }

  removeWidgetFromPage(
    widgetUniqueId: string | number,
    widgetLayoutType?: LayoutNames,
    zone: string = this.activeConfigService.activeState.activeZone,
    pageIndex: number = this.activeConfigService.activeState.activePageIndex,
  ) {
    const { activeDevice, activeRole, activeCanvasSubscription } = this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[zone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const pageConfig = zoneConfig[activeDevice]?.pages[pageIndex];
        const selectedWidgetArray = getWidgetsArrayByLayoutTypeOrWidgetId(
          pageConfig,
          widgetLayoutType,
          widgetUniqueId,
        );

        const widget: NewWorkflowWidgetConfig | null = findWidget(widgetUniqueId, selectedWidgetArray);
        if (widget) {
          this.executeZoneHook(widget, pageIndex, zoneConfig, 'remove');

          this.executeRoleHook(widget, role, zoneConfig, 'remove');

          if (widget.name === WidgetNames.INPUT_WIDGET) {
            this.deleteInputWidget(widgetUniqueId, workflowConfig, widget, selectedWidgetArray);
          } else {
            deleteWidget(widgetUniqueId, selectedWidgetArray);
          }
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to delete');
        }
      }

      return workflowConfig;
    });
  }

  private deleteInputWidget(
    widgetUniqueId: string | number,
    workflowConfig: WorkflowConfig,
    widget: NewWorkflowWidgetConfig,
    selectedWidgetArray: NewWorkflowWidgetConfig[],
  ) {
    deleteWidget(widgetUniqueId, selectedWidgetArray);
    removeFieldsFromProjectFormByIds(
      workflowConfig.permissionSections.projectForms?.find(
        (form) =>
          form.id ===
          widget.widgetConfiguration.attributes.widgetProperties?.inputFieldConfig?.['parentForm'],
      ),
      [widget.widgetConfiguration.attributes.widgetProperties?.inputFieldConfig?.id as string],
    );
    const currentInputCount = computeNumberOfInputs(selectedWidgetArray[0].childComponents);
    selectedWidgetArray[0].widgetConfiguration.attributes.widgetProperties = {
      ...selectedWidgetArray[0].widgetConfiguration.attributes.widgetProperties,
      formsConfig: {
        ...(selectedWidgetArray[0].widgetConfiguration.attributes.widgetProperties
          ?.formsConfig as FormsConfigType),
        numberOfInputs: currentInputCount,
      },
    };
  }

  copyWidgetFromPage(
    widgetUniqueId: string,
    widgetLayoutType?: LayoutNames,
    zone: string = this.activeConfigService.activeState.activeZone,
    pageIndex: number = this.activeConfigService.activeState.activePageIndex,
  ) {
    const { activeDevice, activeRole, activeCanvasSubscription } = this.activeConfigService.activeState;

    const role = this.workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
    const zoneConfig = this.workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
      ?.zones[zone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

    if (role) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      const pageConfig = zoneConfig[activeDevice]?.pages[pageIndex];
      const selectedWidgetArray = getWidgetsArrayByLayoutTypeOrWidgetId(
        pageConfig,
        widgetLayoutType,
        widgetUniqueId,
      );

      const widget = findWidget(widgetUniqueId, selectedWidgetArray);

      if (widget) {
        this.store.dispatch(new CopyWidget(JSON.parse(JSON.stringify(widget))));
        this.alertHandlerService.showAlertFn('success', 'Widget Copied!');
      } else {
        this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to copy');
      }
    }
  }

  public copyWidgetsFromPage(widgetUniqueIds: string[]) {
    const { activeDevice, activeRole, activeCanvasSubscription, activeZone, activePageIndex } =
      this.activeConfigService.activeState;

    const role = this.workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
    const zoneConfig = this.workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
      ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;

    if (role) {
      const page = zoneConfig[activeDevice]?.pages[activePageIndex];
      let flattenedWidgetsArray: NewWorkflowWidgetConfig[] = [];
      if (page) flattenedWidgetsArray = getFlattenedWidgetArray(page);

      if (flattenedWidgetsArray) {
        const widgetsToCopy = this.findWidgetsToCopy(flattenedWidgetsArray, widgetUniqueIds);
        if (widgetsToCopy.length) {
          this.store.dispatch(new CopyWidgets(JSON.parse(JSON.stringify(widgetsToCopy))));
          this.alertHandlerService.showAlertFn('success', 'Widget Copied!');
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to copy');
        }
      } else {
        this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to copy');
      }
    }
  }

  /**
   * Recursively extracts widgets from layout widgets and their children
   * @param widgets - Array of widget configs to process
   * @param widgetUniqueIds - Array of unique IDs to match against
   * @returns Array of widget configs that match the unique IDs
   */
  private findWidgetsToCopy(
    widgets: NewWorkflowWidgetConfig[],
    widgetUniqueIds: string[],
  ): NewWorkflowWidgetConfig[] {
    let result: NewWorkflowWidgetConfig[] = [];

    for (const widget of widgets) {
      // Check if current widget matches any of the target unique IDs
      if (widgetUniqueIds.includes(widget.uniqueId.toString())) {
        result.push(widget);
      }

      // If widget is a layout widget, process its children
      if (isLayoutWidget(widget.name as ComponentLabel) && widget.childComponents) {
        if (is2dArray(widget.childComponents)) {
          // Process 2D array of child components
          (widget.childComponents as NewWorkflowWidgetConfig[][]).forEach((row) => {
            result = [...result, ...this.findWidgetsToCopy(row, widgetUniqueIds)];
          });
        } else if (Array.isArray(widget.childComponents)) {
          // Process 1D array of child components
          result = [
            ...result,
            ...this.findWidgetsToCopy(widget.childComponents as NewWorkflowWidgetConfig[], widgetUniqueIds),
          ];
        }
      }
    }

    // Remove duplicates by keeping only the first occurrence of each uniqueId
    return result.filter(
      (widget, index, self) => index === self.findIndex((w) => w.uniqueId === widget.uniqueId),
    );
  }

  updateImageWidgetConfig(
    uniqueId: string | number,
    response: any,
    fieldName: WidgetNames,
    files: File[] | undefined,
    zoneConfig: WorkflowZoneDevicesConfig,
  ) {
    const widgets =
      zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex].widgets;

    if (widgets) {
      let imageWidget = findWidget(uniqueId, widgets);
      if (!imageWidget) {
        // if image widget does not exist, create a new one
        // its happening because we haven't pushed the image widget config in page widgets as per the expected behavior.
        // so we need to create a new one and push it in the widgets array
        const imageWidgetConfigCopy = cloneDeep(imageWidgetWidgetConfig);
        imageWidgetConfigCopy.uniqueId = uniqueId;
        widgets.push(imageWidgetConfigCopy);
        imageWidget = findWidget(uniqueId, widgets);
      }
      if (imageWidget) {
        imageWidget.widgetConfiguration.attributes.elementsConfig[0].options = [];
        response.forEach((element: any, index: number) => {
          const optionsConfig = {
            id: element.id,
            position: index,
            field_type: 'image',
            field_name: fieldName,
            label: element?.filename ?? files?.[index]?.name,
            type: 'file',
            show: true,
          };
          imageWidget?.widgetConfiguration.attributes.elementsConfig[0].options.push(optionsConfig);
        });
      }
    }
  }

  private getZoneConfig(workflowConfig: WorkflowConfig): WorkflowZoneDevicesConfig {
    return workflowConfig.permissionSections.roles.find((role) => role.name === this.activeConfig.activeRole)
      ?.zones[this.activeConfig.activeZone]?.canvases.subscription[
      this.activeConfig.activeCanvasSubscription
    ] as WorkflowZoneDevicesConfig;
  }

  private updateGridPosition(
    uniqueId: string | number,
    widgets: WorkflowWidgetConfig[],
    gridPosition: GridPosition | undefined,
  ) {
    const widget = widgets.find((widget) => widget.uniqueId === uniqueId);

    if (widget !== undefined && gridPosition) {
      widget.gridPosition = gridPosition;
    }
  }

  updateWidgetPosition(layout: WidgetLayoutPosition[]) {
    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig): WorkflowConfig => {
      const zoneConfig = this.getZoneConfig(workflowConfig);

      const page = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];

      layout.forEach((layout) => {
        if (page?.widgets) this.updateGridPosition(layout.uniqueId, page?.widgets, layout.gridPosition);
      });

      return workflowConfig;
    }, true);
  }

  public updateOneWidgetHideCondition(uniqueId: string | number, hideCondition?: string) {
    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig): WorkflowConfig => {
      const zoneConfig = this.getZoneConfig(workflowConfig);
      const page = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
      if (page?.widgets) {
        const widget = findWidget(uniqueId, page?.widgets);
        if (widget) {
          widget.widgetConfiguration.attributes.hideCondition = hideCondition;
        }
      }

      return workflowConfig;
    });
  }

  updateOuterCssWidget(uniqueId: string | number, outerCss: string) {
    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig): WorkflowConfig => {
      const zoneConfig = this.getZoneConfig(workflowConfig);
      const page = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
      if (page?.widgets) {
        const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(page, undefined, uniqueId);
        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          widget.widgetConfiguration.attributes.outerCss = outerCss;
        }
      }

      return workflowConfig;
    });
  }
  updateChildOuterCssWidget(uniqueId: string | number, outerCss: string, childIndex: number) {
    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig): WorkflowConfig => {
      const zoneConfig = this.getZoneConfig(workflowConfig);
      const page = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
      if (page?.widgets) {
        const widget = findWidget(uniqueId, page?.widgets);
        if (widget) {
          if (!widget.childComponents) {
            this.alertHandlerService.showAlertFn('error', 'No child components found.');
            return workflowConfig;
          }
          //if no previous child css was set, create the empty array
          if (!widget.widgetConfiguration.attributes['childComponentOuterCss']) {
            widget.widgetConfiguration.attributes['childComponentOuterCss'] = [];
          }
          //if a new child was added. eg a new column, but no index exists for it yet in the config, create one
          if (
            widget.widgetConfiguration.attributes['childComponentOuterCss'].length <
            widget.childComponents.length
          ) {
            while (
              widget.widgetConfiguration.attributes['childComponentOuterCss'].length <
              widget.childComponents.length
            ) {
              widget.widgetConfiguration.attributes['childComponentOuterCss'].push('');
            }
          }
          widget.widgetConfiguration.attributes['childComponentOuterCss'][childIndex] = outerCss;
        }
      }

      return workflowConfig;
    });
  }

  public updateOneWidgetPosition(uniqueId: string | number, gridPosition: GridPosition) {
    this.workflowConfigService.updateConfig((workflowConfig: WorkflowConfig): WorkflowConfig => {
      const zoneConfig = this.getZoneConfig(workflowConfig);
      const page = zoneConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
      if (page?.widgets) this.updateGridPosition(uniqueId, page?.widgets, gridPosition);

      return workflowConfig;
    });
  }

  getCurrentPageWidgets(): WorkflowWidgetConfig[] {
    const zoneDevicesConfig = this.workflowConfig.permissionSections?.roles?.[
      this.activeConfig.activeRoleIndex
    ]?.zones[this.activeConfig.activeZone]?.canvases.subscription[
      this.activeConfig.activeCanvasSubscription
    ] as WorkflowZoneDevicesConfig | undefined;

    if (zoneDevicesConfig) {
      return (
        zoneDevicesConfig[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex]
          ?.widgets ?? []
      );
    } else {
      return [];
    }
  }

  /*
    Returns false if detects widget exists already so should exit otherwise always false
  */
  private addWidgetToPageOrAlternativeWidgets(
    widget: WorkflowWidgetConfig,
    zone: WorkflowZoneDevicesConfig,
    page: WorkflowPageConfig,
    widgetLayoutType?: LayoutNames | string | undefined,
  ): boolean {
    const { activeDevice, activePageIndex } = this.activeConfigService.activeState;

    // If alternativeAddWidgets then don't add widget and instead add alterntive list of widgets
    // This allows for a number of extra widget to get added instead of the one dragged
    if (widget?.alternativeAddWidgets?.length) {
      widget.alternativeAddWidgets.forEach((widget) => {
        const pageConfig = zone[activeDevice]?.pages[activePageIndex];
        const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType, widget?.uniqueId);
        // If widgets not already added
        if (widgets && !findWidget(widget.uniqueId, widgets)) {
          const widgetsLength = widgets?.push(widget);
          // Push added widget to bottom of page on y axis
          if (widgetsLength && page && page.pageConfiguration?.isUsingHtmlStructure !== true)
            checkAddWidgetGridPosition(widgetsLength - 1, page);
        }
      });
    } else {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(page, widgetLayoutType, widget?.uniqueId);
      // is widget present then don't add
      if (widgets && findWidget(widget.uniqueId, widgets)) {
        this.alertHandlerService.showAlertFn('error', 'Widget can only be placed once per page.');
        return false;
      }

      // Add the widget to the array returned by getWidgetsArrayByLayoutTypeOrWidgetId
      const widgetsLength = widgets?.push(widget);
      // Push added widget to bottom of page on y axis
      if (widgetsLength && page && page.pageConfiguration?.isUsingHtmlStructure !== true)
        checkAddWidgetGridPosition(widgetsLength - 1, page);
    }

    return true;
  }

  /**
   * @description: function for default global styling for widgets' dropping
   */

  private applyDefaultGlobalStyling(widget: NewWorkflowWidgetConfig) {
    return onSetDefaultButtonGlobalStyling(widget);
  }

  private executeZoneHook(
    widget: WorkflowWidgetConfig,
    pageIndex: number,
    zoneConfig: WorkflowZoneDevicesConfig,
    action: HookAction,
  ) {
    const { activeDevice } = this.activeConfigService.activeState;
    // modify zone if widget requires i.e to add additonal page to zone
    if (widget.zoneHook && zoneConfig[activeDevice]) {
      const hook = zoneHooks[widget.zoneHook];
      if (hook) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        hook(zoneConfig[activeDevice]!, action, pageIndex);
      }
    }
  }

  private executeRoleHook(
    widget: WorkflowWidgetConfig,
    role: Role,
    zoneConfig: WorkflowZoneDevicesConfig,
    action: HookAction,
  ) {
    const { activeDevice, activeCanvasSubscription } = this.activeConfigService.activeState;
    const baasTags = this.store.selectSnapshot(ProjectsState.getActiveProject).relevantBaasTags;
    // modify zone if widget requires i.e to add additonal page to zone
    if (widget.roleHook && zoneConfig[activeDevice]) {
      const hook = roleHooks[widget.roleHook];
      if (hook) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        hook(role, activeDevice, activeCanvasSubscription, action, baasTags);
      }
    }
  }

  public textInputValueChange(value: string, uniqueId: string, key: string) {
    this.textInputValueChanges.next({ value, uniqueId, key });
  }

  // Method to retrieve available themes for a persona
  public getAvailableThemeOptions():
    | {
        value: string;
        isPrimary: boolean;
      }[]
    | undefined {
    const activeRole = this.getRoleConfig(formatNameAsUrl(this.activeConfig.activeRole));
    if (activeRole) {
      return activeRole.themeOptions;
    }
    return undefined;
  }

  // Method to update primary theme for a persona
  public updatePrimaryThemeForPersona(themeName: string): void {
    const activeRole = this.getRoleConfig(formatNameAsUrl(this.activeConfig.activeRole));
    this.workflowConfigService.updateConfig((config) => {
      const role = config.permissionSections.roles.find(
        (role) => role.slug === formatNameAsUrl(this.activeConfig.activeRole),
      );
      if (role && activeRole) {
        role.themeOptions = activeRole.themeOptions.map((option) => {
          return { value: option.value, isPrimary: false };
        });
      }
      if (role) {
        const primary = role.themeOptions.find((option) => {
          return option.value === themeName;
        });

        if (primary) {
          primary.isPrimary = true;
        }
      }
      return config;
    });
  }

  /**
   * Checks widget compatibility based on predefined rules.
   * Executes the rule's handler if an incompatible widget is present on the page.
   */
  private checkWidgetCompatibility(droppedWidgetName: string, pageWidgets: WorkflowWidgetConfig[]) {
    // Use flattenWorkflowWidgets to get all widgets (including nested)
    const allWidgets = flattenWorkflowWidgets(pageWidgets);

    for (const rule of this.widgetCompatibilityChecks) {
      if (droppedWidgetName === rule.widget) {
        const found = allWidgets.some((widget) => rule.incompatibleWith.includes(widget.name));
        if (found && typeof rule.handler === 'function') {
          rule.handler();
        }
      }
    }
  }
}

function checkAddWidgetGridPosition(widgetIndex: number, pageConfig: WorkflowPageConfig) {
  const newWidget = pageConfig.widgets[widgetIndex];

  // Find widgets on the same z-index
  const widgetsOnSameZ = pageConfig.widgets.filter(
    (widget, index) =>
      index !== widgetIndex &&
      (widget.gridPosition?.layerIndex ?? 0) === (newWidget.gridPosition?.layerIndex ?? 0),
  );

  let needsRepositioning = true;
  while (needsRepositioning) {
    needsRepositioning = false;

    for (const existingWidget of widgetsOnSameZ) {
      if (newWidget.gridPosition && existingWidget.gridPosition) {
        if (isOverlapping(newWidget, existingWidget)) {
          // Reposition the new widget
          if (newWidget.gridPosition.x < existingWidget.gridPosition.x + existingWidget.gridPosition.cols) {
            // Move to the right of the existing widget
            newWidget.gridPosition.x = existingWidget.gridPosition.x + existingWidget.gridPosition.cols;
          } else {
            // Move below the existing widget
            newWidget.gridPosition.y = existingWidget.gridPosition.y + existingWidget.gridPosition.rows;
            newWidget.gridPosition.x = 0; // Reset x position when moving to a new row
          }
          needsRepositioning = true;
          break;
        }
      }
    }
  }

  function isOverlapping(widget1: WorkflowWidgetConfig, widget2: WorkflowWidgetConfig) {
    return (
      widget1.gridPosition &&
      widget2.gridPosition &&
      widget1.gridPosition.x < widget2.gridPosition.x + widget2.gridPosition.cols &&
      widget1.gridPosition.x + widget1.gridPosition.cols > widget2.gridPosition.x &&
      widget1.gridPosition.y < widget2.gridPosition.y + widget2.gridPosition.rows &&
      widget1.gridPosition.y + widget1.gridPosition.rows > widget2.gridPosition.y
    );
  }
}
